import { useState } from 'react';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { BienCard } from './BienCard';
import { BienForm } from './BienForm';
import { useBiens, useCreateBien } from './hooks';

/**
 * « Structure immobilière » tab of a dossier: the biens with their full
 * Structure → Niveau → Élément → Quantités hierarchy. All business rules
 * (workflow gates, permissions, four-eyes) stay on the server — the web only
 * renders the server's answers and errors.
 */
export function StructureTab({ dossierId }: { dossierId: string }): JSX.Element {
  const [page, setPage] = useState(1);
  const biensQuery = useBiens(dossierId, { page, pageSize: 10 });
  const createBien = useCreateBien(dossierId);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">Structure immobilière</h2>
        <Button onClick={() => setCreating(true)}>Nouveau bien</Button>
      </div>

      {biensQuery.isLoading ? (
        <div className="py-10 text-center">
          <Spinner label="Chargement des biens…" />
        </div>
      ) : biensQuery.isError ? (
        <Alert variant="error">{toDisplayError(biensQuery.error).message}</Alert>
      ) : biensQuery.data && biensQuery.data.items.length > 0 ? (
        <>
          <div className="space-y-3">
            {biensQuery.data.items.map((bien) => (
              <BienCard key={bien.id} bien={bien} />
            ))}
          </div>
          {biensQuery.data.totalPages > 1 ? (
            <Pagination
              page={biensQuery.data.page}
              totalPages={biensQuery.data.totalPages}
              total={biensQuery.data.total}
              onPageChange={setPage}
            />
          ) : null}
        </>
      ) : (
        <p className="rounded-md border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
          Aucun bien pour ce dossier. Créez le premier bien pour décrire la structure immobilière.
        </p>
      )}

      <Dialog open={creating} onClose={() => setCreating(false)} title="Nouveau bien">
        <div className="space-y-4">
          {createBien.isError ? (
            <Alert variant="error" details={toDisplayError(createBien.error).details}>
              {toDisplayError(createBien.error).message}
            </Alert>
          ) : null}
          <BienForm
            pending={createBien.isPending}
            onCancel={() => setCreating(false)}
            onSubmit={(input) => createBien.mutate(input, { onSuccess: () => setCreating(false) })}
          />
        </div>
      </Dialog>
    </div>
  );
}
